import React from 'react';
import { Card, Tag, Typography } from 'antd';
import { tokens } from '../../theme/tokens';
import { Banner } from '../../types/banner';
import { useModal } from '../../context/modal';
import { getBannerStatusData, getDisplayContent, getFirstImage, getFirstOptimizedImage } from '../../utils';
import { useResponsiveSizes } from '../../hooks/use-responsive-sizes';

import { getClientHandler } from '../../handlers';
import { useClientId } from '../../hooks/use-client-id';
import { BannerMetadataLine } from '../../types/library-metadata';

const { Text } = Typography;

const getBannerMetadata = (banner: Banner, clientId: string): BannerMetadataLine[] => {
  const clientHandler = getClientHandler(clientId);

  if (clientHandler.getBannerMetadata) {
    const customLines = clientHandler.getBannerMetadata(banner);
    if (customLines && customLines.length > 0) {
      return customLines;
    }
  }

  // Fallback
  return [{ text: banner.title || 'Asset', fontWeight: 'bold' }];
};

export const BannerCard: React.FC<{ banner: Banner }> = ({ banner }) => {
  const { openAsset } = useModal();
  const { fontSize, spacing, screens } = useResponsiveSizes();
  const [isHovered, setIsHovered] = React.useState(false);

  const image = getFirstOptimizedImage(banner) || getFirstImage(banner);
  const statusData = getBannerStatusData(banner);
  const clientId = useClientId();
  const metadataLines = getBannerMetadata(banner, clientId);

  const cardStyle = {
    width: '100%',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    position: 'relative' as const,
    overflow: 'hidden' as const
  };

  const imageStyle = {
    width: '100%',
    height: 'auto',
    display: 'block',
    backgroundColor: image ? 'transparent' : '#f5f5f5',
    filter: isHovered ? 'brightness(0.6)' : 'none',
    transition: 'all 0.3s ease'
  };

  const overlayStyle = {
    position: 'absolute' as const,
    bottom: 0,
    left: 0,
    right: 0,
    background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.6) 80%, transparent 100%)',
    padding: '32px 12px 12px 12px',
    transform: isHovered ? 'translateY(0)' : 'translateY(100%)',
    transition: 'transform 0.5s ease',
    zIndex: 3
  };

  const placeholderStyle = {
    width: '100%',
    height: screens.xs ? 100 : screens.sm ? 150 : 200,
    backgroundColor: '#f5f5f5',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    color: '#bfbfbf',
    fontSize: fontSize.sm,
    fontWeight: 500,
    gap: `${spacing.xs}px`,
    boxSizing: 'border-box' as const
  };

  return (
    <div style={{ width: '100%' }} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      <Card
        style={cardStyle}
        styles={{
          body: {
            padding: 0,
            height: '100%',
            width: '100%',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
        onClick={() => openAsset(banner.bannerId)}
        hoverable
      >
        {/* Status badge */}
        <div
          style={{
            position: 'absolute',
            top: spacing.xs,
            left: spacing.xs,
            zIndex: 4
          }}
        >
          <Tag
            style={{
              height: spacing.sm + 5,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              backgroundColor: tokens.colors.primary.white,
              fontSize: fontSize.xs,
              padding: `0 ${spacing.xs}px`,
              border: 'none',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            {statusData?.label || 'Enviado'}
            <span
              style={{
                width: spacing.xs,
                height: spacing.xs,
                borderRadius: '50%',
                background: statusData?.background || 'gray'
              }}
            />
          </Tag>
        </div>

        {/* Imagen o placeholder */}
        {image ? (
          <img src={image} alt={getDisplayContent(banner)} style={imageStyle} />
        ) : (
          <div style={placeholderStyle}>
            <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5' style={{ opacity: 0.5 }}>
              <rect x='3' y='3' width='18' height='18' rx='2' ry='2' />
              <circle cx='8.5' cy='8.5' r='1.5' />
              <polyline points='21 15 16 10 5 21' />
            </svg>
            <span>Sin imagen</span>
          </div>
        )}

        <div style={overlayStyle}>
          {metadataLines.map((line, index) => {
            const isFirst = index === 0;

            return (
              <Text
                key={index}
                strong={line.fontWeight === 'bold' || (isFirst && !line.fontWeight)}
                style={{
                  fontSize: line.fontSize ? fontSize[line.fontSize] : fontSize.base,
                  display: 'block',
                  color: line.color || (isFirst ? '#ffffff' : 'rgba(255,255,255,0.9)'),
                  marginBottom: index < metadataLines.length - 1 ? spacing.xs : 0,
                  textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'normal'
                }}
              >
                {line.text}
              </Text>
            );
          })}
        </div>
      </Card>
    </div>
  );
};
